import { useEffect, useState, FunctionComponent } from "react";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import {
  returnVatOfPriceOnly,
  returnPriceMinusVat,
  returnVatOfPrice,
} from "../../../core/helpers";
import {
  selectFormArr,
  selectRegistrationDetail,
  selectPlateFixture,
  setPlateFixture,
  setPlateFixtureValue,
  selectPlateFixtureValue,
  selectCalcPrice,
  selectCreditCardMessage,
  setFinancePrice,
} from "../checkoutPaymentSlice";
import { numberWithCommas } from "../../../core/helpers/formatHelper";

type Props = {
  registration: string;
  isFinance: boolean;
};

const POUND = "\u00A3";

const ORDER_SPECIAL_DELIVERY = 16.74;
const ORDER_RECORDED_DELIVERY = 13.14;
const STANDARD_DELIVERY = 10.74;
const ORDER_TRANSFER_FEE_STANDARD = 29.4;

const PriceBreakdown: FunctionComponent<Props> = (props: Props) => {
  const dispatch = useAppDispatch();
  const { registration, isFinance } = props;

  const plateFixture = useAppSelector(selectPlateFixture);
  const plateFixtureValue = useAppSelector(selectPlateFixtureValue);

  const registrationDetail = useAppSelector(selectRegistrationDetail);
  const formArr = useAppSelector(selectFormArr);
  const calcPrice = useAppSelector(selectCalcPrice);
  const creditCardMessage = useAppSelector(selectCreditCardMessage);

  const [registrationNoVatPrice, setRegistrationNoVatPrice] = useState(0);
  const [registrationVatPrice, setRegistrationVatPrice] = useState(0);
  const [dotPrice, setDotPrice] = useState(0);
  const [dotNoVatPrice, setDotNoVatPrice] = useState(0);
  const [dotVatPrice, setDotVatPrice] = useState(0);

  // plate price
  const [platePrice, setPlatePrice] = useState(0);
  const [plateNoVatPrice, setPlateNoVatPrice] = useState(0);
  const [plateVatPrice, setPlateVatPrice] = useState(0);

  // transfer price
  const [transferPrice, setTransferPrice] = useState(0);
  const [transferNoVatPrice, setTransferNoVatPrice] = useState(0);
  const [transferVatPrice, setTransferVatPrice] = useState(0);

  // card fee
  const [cardFee, setCardFee] = useState(0);
  const [totalPriceIncCardFee, setTotalPriceIncCardFee] = useState(0);

  // total price
  const [totalVatPrice, setTotalVatPrice] = useState(0);
  const [totalNoVatPrice, setTotalNoVatPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [totalPriceWithFixtureAndVat, setTotalPriceFinal] = useState(0);

  // delivery price
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [deliveryNoVatPrice, setDeliveryNoVatPrice] = useState(0);
  const [deliveryVatPrice, setDeliveryVatPrice] = useState(0);
  const [deliveryPriceWording, setDeliveryPriceWording] = useState("");

  // others
  const [giftWrapPriceVat, setGiftWrapPriceVat] = useState(0);
  const [giftWrapPriceExVat, setGiftWrapPriceExVat] = useState(0);
  const [giftWrapPrice, setGiftWrapPrice] = useState(0);
  const [totalVat, setTotalVat] = useState(0);

  useEffect(() => {
    let _totalNoVatPrice = 0;
    let _totalVatPrice = 0;
    let _totalPrice = 0;
    let _cardFee = 0;
    let _deliveryPrice = 0;
    let _deliveryNoVatPrice = 0;
    let _deliveryVatPrice = 0;
    let _transferPrice = 0;
    let _transferVatPrice = 0;
    let _transferNoVatPrice = 0;
    let _giftWrapPriceVat = 0;
    let _giftWrapPriceExVat = 0;
    let _giftWrapPrice = 0;
    let _registrationVatPrice = 0;
    let _registrationNoVatPrice = 0;
    let _platePrice = 0;
    let _platesVatPrice = 0;
    let _platesNoVatPrice = 0;
    let _totalPriceIncCardFee = 0;
    let _subTotal = 0;
    let _totalVat = 0;
    let _totalPriceWithFixtureAndVat = 0;
    let _dotPrice = 0;
    let _dotNoVatPrice = 0;
    let _dotVatPrice = 0;

    // site_Pricing_Dvla
    if (registrationDetail?.sourcecode === "1") {
      _registrationVatPrice = parseFloat(registrationDetail.vat);
      setRegistrationNoVatPrice(parseFloat(registrationDetail.price));
      setDotPrice(parseFloat(registrationDetail.transfer_fee));
      setDotNoVatPrice(parseFloat(registrationDetail.transfer_fee));
      setRegistrationVatPrice(_registrationVatPrice);

      // ACRYLIC PLATES
      if (formArr && formArr.hasOwnProperty("acrylic_plate_total")) {
        _platePrice = parseFloat(formArr.acrylic_plate_total);
        setPlatePrice(_platePrice);
      } else {
        _platePrice = 31.14;
        setPlatePrice(_platePrice);
      }
      _platesVatPrice = returnVatOfPrice(_platePrice);
      _platesNoVatPrice = returnPriceMinusVat(_platePrice);
      setPlateNoVatPrice(_platesNoVatPrice);
      setPlateVatPrice(_platesVatPrice);
      // card fee
      _cardFee = formArr?.CreditCardFee
        ? parseFloat(formArr?.CreditCardFee)
        : 0;
      setCardFee(_cardFee);

      // transfer service
      if (formArr?.transfer_type && formArr.transfer_type !== "none") {
        _transferPrice = ORDER_RECORDED_DELIVERY;
        _transferNoVatPrice = returnPriceMinusVat(ORDER_TRANSFER_FEE_STANDARD);
        _transferVatPrice = returnVatOfPrice(ORDER_TRANSFER_FEE_STANDARD);

        setTransferPrice(_transferPrice);
        setTransferNoVatPrice(_transferNoVatPrice);
        setTransferVatPrice(_transferVatPrice);
      }

      // delivery price
      if (formArr?.delivery_type) {
        if (formArr.delivery_type === "special") {
          _deliveryPrice = ORDER_SPECIAL_DELIVERY;
          _deliveryNoVatPrice = returnPriceMinusVat(_deliveryPrice);
          _deliveryVatPrice = returnVatOfPrice(_deliveryPrice);
          setDeliveryPriceWording("Special delivery");
        } else if (formArr.delivery_type === "recorded") {
          _deliveryPrice = ORDER_RECORDED_DELIVERY;
          _deliveryNoVatPrice = returnPriceMinusVat(_deliveryPrice);
          _deliveryVatPrice = returnVatOfPrice(_deliveryPrice);
          setDeliveryPriceWording("Recorded delivery");
        } else if (formArr.delivery_type === "standard") {
          _deliveryPrice = STANDARD_DELIVERY;
          _deliveryNoVatPrice = returnPriceMinusVat(_deliveryPrice);
          _deliveryVatPrice = returnVatOfPrice(_deliveryPrice);
          _deliveryVatPrice = returnVatOfPriceOnly(_deliveryNoVatPrice);
          setDeliveryPriceWording("Standard delivery");
        }
      }

      setDeliveryPrice(_deliveryPrice);
      setDeliveryNoVatPrice(_deliveryNoVatPrice);
      setDeliveryVatPrice(_deliveryVatPrice);

      // total no vat price
      _totalNoVatPrice =
        parseFloat(registrationDetail.price) +
        parseFloat(registrationDetail.transfer_fee) +
        returnPriceMinusVat(_platePrice) +
        _deliveryNoVatPrice +
        _transferNoVatPrice;
      setTotalNoVatPrice(_totalNoVatPrice);

      // total vat price
      _totalVatPrice =
        parseFloat(registrationDetail.vat) +
        returnVatOfPrice(_platePrice) +
        _deliveryVatPrice +
        _transferVatPrice;
      setTotalVatPrice(_totalVatPrice);

      // total price
      setTotalPrice(_totalNoVatPrice + _totalVatPrice);

      // total price inc card fee
      _totalPriceIncCardFee = _totalNoVatPrice + _totalVatPrice + _cardFee;
      setTotalPriceIncCardFee(_totalPriceIncCardFee);

      // others
      if (formArr?.gift_wrapping === "yes") {
        _giftWrapPriceVat = 1.19;
        _giftWrapPriceExVat = 5.95;
        _giftWrapPrice = 7.14;
        setGiftWrapPriceVat(1.19);
        setGiftWrapPriceExVat(5.95);
        setGiftWrapPrice(7.14);
      }

      // total vat
      _totalVat =
        _registrationVatPrice +
        _deliveryVatPrice +
        _platesVatPrice +
        _transferVatPrice +
        _giftWrapPriceVat;
      setTotalVat(_totalVat);
      //sub total
      _subTotal = _totalPriceIncCardFee - _totalVat;
      if (formArr?.gift_wrapping === "yes") {
        _subTotal = _subTotal + _giftWrapPrice;
      }
      setSubTotal(_subTotal);

      // total price with fixture and vat
      _totalPriceWithFixtureAndVat = _totalPriceIncCardFee + _giftWrapPrice;
      setTotalPriceFinal(_totalPriceWithFixtureAndVat);
    } else if (registrationDetail?.sourcecode === "134") {
      // calculate price when sourcecode is 134
      // site pricing stock

      // registration prices
      if (
        registrationDetail?.originalPrice &&
        registrationDetail.originalPrice !== registrationDetail.price
      ) {
        _registrationNoVatPrice = parseFloat(registrationDetail.originalPrice);
      } else {
        _registrationNoVatPrice = parseFloat(registrationDetail.price);
      }
      _registrationVatPrice = parseFloat(registrationDetail?.vat);

      setRegistrationNoVatPrice(_registrationNoVatPrice);
      setRegistrationVatPrice(_registrationVatPrice);

      // dot price
      _dotPrice = parseFloat(registrationDetail?.transfer_fee);
      _dotVatPrice = returnVatOfPrice(_dotPrice);
      _dotNoVatPrice = returnPriceMinusVat(_dotPrice);
      setDotPrice(_dotPrice);
      setDotVatPrice(_dotVatPrice);
      setDotNoVatPrice(_dotNoVatPrice);

      // acrylic plates
      _platePrice =
        formArr?.order_plate && formArr.order_plate === "yes"
          ? parseFloat(formArr.acrylic_plate_total)
          : 0;
      _platesNoVatPrice =
        _platePrice > 0 ? returnPriceMinusVat(_platePrice) : 0;
      _platesVatPrice = _platePrice > 0 ? returnVatOfPrice(_platePrice) : 0;
      setPlatePrice(_platePrice);
      setPlateNoVatPrice(_platesNoVatPrice);
      setPlateVatPrice(_platesVatPrice);

      // free postage
      _deliveryPrice = 0;
      _deliveryNoVatPrice = 0;
      _deliveryVatPrice = 0;
      setDeliveryPrice(_deliveryPrice);
      setDeliveryNoVatPrice(_deliveryNoVatPrice);
      setDeliveryVatPrice(_deliveryVatPrice);

      // gift wrap
      if (formArr?.gift_wrapping === "yes") {
        _giftWrapPriceVat = 1.19;
        _giftWrapPriceExVat = 5.95;
        _giftWrapPrice = 7.14;
      }
      setGiftWrapPriceExVat(_giftWrapPriceExVat);
      setGiftWrapPriceVat(_giftWrapPriceVat);
      setGiftWrapPrice(_giftWrapPrice);

      // total price
      _totalNoVatPrice =
        _registrationNoVatPrice +
        _platesNoVatPrice +
        _deliveryNoVatPrice +
        _dotNoVatPrice;
      _totalVatPrice =
        _registrationVatPrice +
        _platesVatPrice +
        _deliveryVatPrice +
        _dotVatPrice;
      _totalPrice = _totalNoVatPrice + _totalVatPrice;
      _totalPriceWithFixtureAndVat = _totalPrice;
      setTotalNoVatPrice(_totalNoVatPrice);
      setTotalVatPrice(_totalVatPrice);
      setTotalPrice(_totalPrice);
      setTotalPriceFinal(_totalPriceWithFixtureAndVat);
    } else if (registrationDetail) {
      // site_Pricing_Standard
      // registration prices
      if (
        registrationDetail.originalPrice &&
        registrationDetail.originalPrice !== registrationDetail.price
      ) {
        _registrationNoVatPrice = parseFloat(registrationDetail.originalPrice);
      } else {
        _registrationNoVatPrice = parseFloat(registrationDetail.price);
      }
      _registrationVatPrice = parseFloat(registrationDetail?.vat);
      setRegistrationNoVatPrice(_registrationNoVatPrice);
      setRegistrationVatPrice(_registrationVatPrice);

      // ACRYLIC PLATES
      _platePrice =
        formArr?.order_plate && formArr.order_plate === "yes"
          ? parseFloat(formArr.acrylic_plate_total)
          : 0;
      _platesNoVatPrice =
        _platePrice > 0 ? returnPriceMinusVat(_platePrice) : 0;
      _platesVatPrice = _platePrice > 0 ? returnVatOfPrice(_platePrice) : 0;
      setPlatePrice(_platePrice);
      setPlateNoVatPrice(_platesNoVatPrice);
      setPlateVatPrice(_platesVatPrice);

      // DVLA TRANSFER FEE
      _dotPrice = parseFloat(registrationDetail.transfer_fee);
      _dotNoVatPrice = parseFloat(registrationDetail.transfer_fee);
      _dotVatPrice = !registrationDetail.web_vat
        ? 0
        : returnVatOfPriceOnly(_dotPrice);
      setDotPrice(_dotPrice);
      setDotVatPrice(_dotVatPrice);
      setDotNoVatPrice(_dotNoVatPrice);

      // free postage
      _deliveryPrice = 0;
      _deliveryNoVatPrice = 0;
      _deliveryVatPrice = 0;
      setDeliveryPrice(_deliveryPrice);
      setDeliveryNoVatPrice(_deliveryNoVatPrice);
      setDeliveryVatPrice(_deliveryVatPrice);

      // gift wrap
      if (formArr?.gift_wrapping === "yes") {
        _giftWrapPriceVat = 1.19;
        _giftWrapPriceExVat = 5.95;
        _giftWrapPrice = 7.14;
      }
      setGiftWrapPriceExVat(_giftWrapPriceExVat);
      setGiftWrapPriceVat(_giftWrapPriceVat);
      setGiftWrapPrice(_giftWrapPrice);

      // total price
      _totalNoVatPrice =
        _registrationNoVatPrice +
        _platesNoVatPrice +
        _deliveryNoVatPrice +
        _dotNoVatPrice;
      _totalVatPrice =
        _registrationVatPrice +
        _platesVatPrice +
        _deliveryVatPrice +
        _dotVatPrice;
      _totalPrice = _totalNoVatPrice + _totalVatPrice;
      _totalPriceWithFixtureAndVat = _totalPrice;
      setTotalNoVatPrice(_totalNoVatPrice);
      setTotalVatPrice(_totalVatPrice);
      setTotalPrice(_totalPrice);
      setTotalPriceFinal(_totalPriceWithFixtureAndVat);

      // card fee
      _cardFee = formArr?.CreditCardFee ? parseFloat(formArr.CreditCardFee) : 0;
      _totalPriceIncCardFee = _totalNoVatPrice + _totalVatPrice + _cardFee;
      setCardFee(_cardFee);
      setTotalPriceIncCardFee(_totalPriceIncCardFee);
    }

    // old logic in jQuery, set total price to calc price
    setTotalPrice(calcPrice);
    setTotalPriceFinal(calcPrice);
  }, [registrationDetail]);

  useEffect(() => {
    plateFixtureValue === 0
      ? setTotalPriceFinal(totalPrice)
      : setTotalPriceFinal(Number((parseFloat(totalPrice.toString()) + plateFixtureValue).toFixed(2)));
  }, [plateFixtureValue]);

  useEffect(() => {
    if (isFinance) {
      dispatch(setFinancePrice(totalPriceWithFixtureAndVat));
    }
  }, [totalPriceWithFixtureAndVat, isFinance]);

  return (
    <div className="p-6 bg-card-bg-grey">
      <div className="">
        <p className="text-xl font-bold" style={{ color: "#358639" }}>
          Price Breakdown:
        </p>
        {registrationDetail?.sourcecode === "1" ? (
          <>
            {isFinance && (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Finance arrangement fee</span>
                <span className="">{POUND}0</span>
              </p>
            )}
            <p className="text-base font-bold flex align-center justify-between border-b py-4 px-1">
              <span>Registration Mark</span>
              <span className="">
                {POUND}
                {numberWithCommas(registrationNoVatPrice)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Compulsory DVLA transfer fee</span>
              <span className="">
                {POUND}
                {numberWithCommas(dotNoVatPrice)}
              </span>
            </p>
            {transferNoVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Transfer & administration</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(transferNoVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {plateNoVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Acrylic plates</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(plateNoVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {deliveryPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>{deliveryPriceWording}</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(deliveryNoVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {formArr?.gift_wrapping === "yes" ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Gift Wrapping</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(giftWrapPriceExVat)}
                </span>
              </p>
            ) : (
              <></>
            )}
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Subtotal</span>
              <span className="">
                {POUND}
                {numberWithCommas(subTotal)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Total VAT @ 20%</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalVat)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Total Price</span>
              <span className="">
                {POUND}
                {/* according to old logic in jQuery, total price set to calc price*/}
                {numberWithCommas(totalPrice)}
                {/* {numberWithCommas(totalPriceIncCardFee + giftWrapPrice)} */}
              </span>
            </p>
            <p
              className={`text-base flex align-center justify-between border-b py-4 px-1 ${
                plateFixtureValue === 0 ? "hidden" : ""
              }`}
            >
              <span>Total Price ( including fixtures and VAT )</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPriceWithFixtureAndVat)}
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
        {registrationDetail?.sourcecode === "134" ? (
          <>
            {isFinance && (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Finance arrangement fee</span>
                <span className="">{POUND}0</span>
              </p>
            )}
            <p className="text-base font-bold flex align-center justify-between border-b py-4 px-1">
              <span>Registration Mark</span>
              <span className="">
                {POUND}
                {numberWithCommas(registrationNoVatPrice)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Compulsory DVLA transfer fee</span>
              <span className="">
                {POUND}
                {numberWithCommas(dotPrice)}
              </span>
            </p>
            {registrationVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>VAT @ 20%</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(registrationVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {plateNoVatPrice > 0 ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Standard plates</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(plateNoVatPrice)}
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on physical plates</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(plateVatPrice)}
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            {formArr?.gift_wrapping === "yes" ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceExVat)}
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceVat)}
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Total Price</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPrice)}
              </span>
            </p>
            <p
              className={`text-base flex align-center justify-between border-b py-4 px-1 ${
                plateFixtureValue === 0 ? "hidden" : ""
              }`}
            >
              <span>Total Price ( including fixtures and VAT )</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPriceWithFixtureAndVat)}
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
        {registrationDetail?.sourcecode !== "134" &&
        registrationDetail?.sourcecode !== "1" ? (
          <>
            {isFinance && (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>Finance arrangement fee</span>
                <span className="">{POUND}0</span>
              </p>
            )}
            <p className="text-base font-bold flex align-center justify-between border-b py-4 px-1">
              <span>Registration Mark</span>
              <span className="">
                {POUND}
                {numberWithCommas(registrationNoVatPrice)}
              </span>
            </p>
            <p className="text-base flex align-center justify-between border-b py-4 px-1">
              <span>Compulsory DVLA transfer fee</span>
              <span className="">
                {POUND}
                {numberWithCommas(dotPrice)}
              </span>
            </p>
            {registrationVatPrice > 0 ? (
              <p className="text-base flex align-center justify-between border-b py-4 px-1">
                <span>VAT @ 20%</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(registrationVatPrice)}
                </span>
              </p>
            ) : (
              <></>
            )}
            {plateNoVatPrice > 0 ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Standard plates</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(plateNoVatPrice)}
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on physical plates</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(plateVatPrice)}
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            {formArr?.gift_wrapping === "yes" ? (
              <>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceExVat)}
                  </span>
                </p>
                <p className="text-base flex align-center justify-between border-b py-4 px-1">
                  <span>VAT on Gift Wrapping</span>
                  <span className="">
                    {POUND}
                    {numberWithCommas(giftWrapPriceVat)}
                  </span>
                </p>
              </>
            ) : (
              <></>
            )}
            <div className="text-base font-bold py-2 border-b">
              <p className="flex align-center justify-between py-2">
                <span>Total Price:</span>
                <span className="">
                  {POUND}
                  {numberWithCommas(totalPrice)}
                </span>
              </p>
            </div>
            <p
              className={`text-base flex align-center justify-between border-b py-4 px-1 ${
                plateFixtureValue === 0 ? "hidden" : ""
              }`}
            >
              <span>Total Price ( including fixtures and VAT )</span>
              <span className="">
                {POUND}
                {numberWithCommas(totalPriceWithFixtureAndVat)}
              </span>
            </p>
          </>
        ) : (
          <></>
        )}
        {!isFinance && (
          <p
            dangerouslySetInnerHTML={{ __html: creditCardMessage }}
            className="text-xs border-b py-4 px-1"
          ></p>
        )}
      </div>
      <div className={`mt-4 ${formArr?.order_plate !== "no" ? "" : "hidden"}`}>
        <p className="text-base font-bold mb-4">
          Add fixtures for your plates:
        </p>
        <div className="grid lg:grid-cols-2 md:grid-cols-1 gap-8">
          <div
            className={`border border-solid border-slate-300 hover:border-lime-600 rounded cursor-pointer p-4 flex flex-col justify-between ${
              plateFixture === "screw-fixtures" ? "border-lime-600" : ""
            }`}
            style={{ backgroundColor: "#eeeeee", minHeight: "20rem" }}
            onClick={() => {
              if (plateFixture === "screw-fixtures") {
                dispatch(setPlateFixture(""));
                dispatch(setPlateFixtureValue(0));
              } else {
                dispatch(setPlateFixture("screw-fixtures"));
                dispatch(setPlateFixtureValue(2.95 * 1.2));
              }
            }}
          >
            <div className="">
              <p className="text-lg font-bold flex align-center justify-between">
                <span>Screw Fixtures</span>
                <span className="text-lime-600">{POUND}2.95</span>
              </p>
              <ul className="list-disc px-4 mt-4 text-sm">
                <li className="mt-2">Complete plate fixing set</li>
                <li className="mt-2">For direct surface mounting</li>
                <li className="mt-2">Coloured cap cover included</li>
              </ul>
            </div>

            <div className="w-full flex justify-center">
              <div className="font-bold border border-solid border-lime-600 border-emerald-600 rounded text-center p-2 text-lime-600 w-10/12">
                Add This
              </div>
            </div>
          </div>
          <div
            className={`border border-solid border-slate-300 hover:border-lime-600 rounded cursor-pointer p-4 flex flex-col justify-between ${
              plateFixture === "pad-fixtures" ? "border-lime-600" : ""
            }`}
            style={{ backgroundColor: "#eeeeee", minHeight: "20rem" }}
            onClick={() => {
              if (plateFixture === "pad-fixtures") {
                dispatch(setPlateFixture(""));
                dispatch(setPlateFixtureValue(0));
              } else {
                dispatch(setPlateFixture("pad-fixtures"));
                dispatch(setPlateFixtureValue(4.95 * 1.2));
              }
            }}
          >
            <div className="">
              <p className="text-lg font-bold flex align-center justify-between">
                <span>Adhesive Pads</span>
                <span className="text-lime-600">{POUND}4.95</span>
              </p>
              <ul className="list-disc px-4 mt-4 text-sm">
                <li className="mt-2">Quick easy number plate fixings</li>
                <li className="mt-2">No drilling or screws required</li>
                <li className="mt-2">High adhesive for long lasting grip</li>
                <li className="mt-2">
                  Recommended for Acrylic & Plastic number plates
                </li>
                <li className="mt-2">Secure Fixing</li>
              </ul>
            </div>

            <div className="w-full flex justify-center">
              <div className="font-bold border border-solid border-lime-600 border-emerald-600 rounded text-center p-2 text-lime-600 w-10/12">
                Add This
              </div>
            </div>
          </div>
        </div>
      </div>
      {formArr?.order_plate === "yes" &&
        registrationDetail?.sourcecode !== "134" &&
        registrationDetail?.sourcecode !== "1" && (
          <p className="text-xs py-1 px-1">
            We will also provide you with a new set of high quality acrylic
            plates displaying{" "}
            <strong>{registrationDetail?.registration}</strong> delivered
            directly to your door within 7 working days upon receipt of the
            requested identity documents. These will be manufactured in line
            with our{" "}
            <a
              target="_blank"
              href="https://www.newreg.co.uk/services/terms_and_conditions.html"
              rel="noreferrer"
              className="text-[#2FA1FF]"
            >
              Terms and Conditions
            </a>
            .
          </p>
        )}
    </div>
  );
};

export { PriceBreakdown, PriceBreakdown as default };
